import React, { useState, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Key, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Hooks
import useAuth from "./hooks/useAuth";
import useApi from "./hooks/useApi";

// Shared Components
import Sidebar from "./components/shared/Sidebar";

// Pages
import LoginPage from "./pages/LoginPage";
import { PlatformPage, PolicyPage } from "./pages/PublicInfoPage";
import DashboardPage from "./pages/DashboardPage";
const SitesListPage = lazy(() => import("./pages/SitesListPage"));
const SiteDetailPage = lazy(() => import("./pages/SiteDetailPage"));
const LocationsPage = lazy(() => import("./pages/LocationsPage"));
const LocationDetailPage = lazy(() => import("./pages/LocationDetailPage"));
const NetworkPage = lazy(() => import("./pages/NetworkPage"));
const UsersPage = lazy(() => import("./pages/UsersPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const IntegrationsPage = lazy(() => import("./pages/IntegrationsPage"));
const MultiViewsPage = lazy(() => import("./pages/MultiViewsPage"));
const MultiViewDetailPage = lazy(() => import("./pages/MultiViewDetailPage"));
const DevLogPage = lazy(() => import("./pages/DevLogPage"));
const TranscoderChannelDetailPage = lazy(() => import("./pages/ChannelDetailPage"));
const TranscodersPage = lazy(() => import("./pages/TranscodersPage"));
const TranscoderFleetPage = lazy(() => import("./pages/TranscoderFleetPage"));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));
const BoxDetailPage = lazy(() => import("./pages/BoxDetailPage"));
const EdgeDetailPage = lazy(() => import("./pages/EdgeDetailPage"));
const ApiReferencePage = lazy(() => import("./pages/ApiReferencePage"));
const DocumentationPage = lazy(() => import("./pages/DocumentationPage"));
const PerformancePage = lazy(() => import("./pages/PerformancePage"));
const ChannelAnalyticsPage = lazy(() => import("./pages/ChannelAnalyticsPage"));
const RadioBoxesPage = lazy(() => import("./pages/radio/RadioBoxesPage"));
const RadioFeedsPage = lazy(() => import("./pages/radio/RadioFeedsPage"));
const RadioFeedDetailPage = lazy(() => import("./pages/radio/RadioFeedDetailPage"));
const RadioOperatorsPage = lazy(() => import("./pages/radio/RadioOperatorsPage"));

// Main Layout
const MainLayout = ({ children, currentPage, onLogout, user, api }) => {
  return (
    <div className="flex h-[100dvh] flex-col bg-zinc-950">
      <Sidebar currentPage={currentPage} onLogout={onLogout} user={user} api={api} />
      <main
        className="min-h-0 min-w-0 flex-1 overflow-auto"
        data-testid="studio-main-content"
      >
        <Suspense fallback={
          <div className="flex h-full items-center justify-center text-sm text-zinc-400">
            Loading Studio…
          </div>
        }>
          {children}
        </Suspense>
      </main>
    </div>
  );
};

// App Router
const AppRouter = ({ auth }) => {
  const { getAuthHeader, logout, user } = auth;
  const api = useApi(getAuthHeader);

  return (
    <Routes>
      <Route path="/platform" element={<PlatformPage />} />
      <Route path="/terms" element={<PolicyPage policy="terms" />} />
      <Route path="/privacy" element={<PolicyPage policy="privacy" />} />
      <Route path="/acceptable-use" element={<PolicyPage policy="acceptable-use" />} />
      <Route path="/feed-provider-agreement" element={<PolicyPage policy="feed-provider-agreement" />} />
      <Route path="/" element={
        <MainLayout currentPage="dashboard" onLogout={logout} user={user} api={api}>
          <DashboardPage api={api} />
        </MainLayout>
      } />
      <Route path="/sites" element={
        <MainLayout currentPage="sites" onLogout={logout} user={user} api={api}>
          <SitesListPage api={api} user={user} />
        </MainLayout>
      } />
      <Route path="/sites/:siteId" element={
        <MainLayout currentPage="sites" onLogout={logout} user={user} api={api}>
          <SiteDetailPage api={api} user={user} />
        </MainLayout>
      } />
      <Route path="/locations" element={
        <MainLayout currentPage="locations" onLogout={logout} user={user} api={api}>
          <LocationsPage api={api} user={user} />
        </MainLayout>
      } />
      <Route path="/locations/:locationId" element={
        <MainLayout currentPage="locations" onLogout={logout} user={user} api={api}>
          <LocationDetailPage api={api} user={user} />
        </MainLayout>
      } />
      <Route path="/network" element={
        <MainLayout currentPage="network" onLogout={logout} user={user} api={api}>
          <NetworkPage api={api} />
        </MainLayout>
      } />
      <Route path="/users" element={
        <MainLayout currentPage="users" onLogout={logout} user={user} api={api}>
          <UsersPage api={api} user={user} />
        </MainLayout>
      } />
      <Route path="/multiviews" element={
        <MainLayout currentPage="multiviews" onLogout={logout} user={user} api={api}>
          <MultiViewsPage api={api} />
        </MainLayout>
      } />
      <Route path="/multiviews/:mvId" element={
        <MainLayout currentPage="multiviews" onLogout={logout} user={user} api={api}>
          <MultiViewDetailPage api={api} />
        </MainLayout>
      } />
      <Route path="/distribution" element={
        <MainLayout currentPage="transcoders" onLogout={logout} user={user} api={api}>
          <TranscodersPage api={api} user={user} />
        </MainLayout>
      } />
      <Route path="/distribution/dvr" element={
        <MainLayout currentPage="transcoders" onLogout={logout} user={user} api={api}>
          <TranscodersPage api={api} user={user} initialTab="dvr" />
        </MainLayout>
      } />
      <Route path="/transcoders" element={
        <MainLayout currentPage="transcoders" onLogout={logout} user={user} api={api}>
          <TranscodersPage api={api} user={user} />
        </MainLayout>
      } />
      <Route path="/transcoders/fleet" element={
        <MainLayout currentPage="transcoders" onLogout={logout} user={user} api={api}>
          <TranscoderFleetPage api={api} user={user} />
        </MainLayout>
      } />
      <Route path="/distribution/fleet" element={
        <MainLayout currentPage="transcoders" onLogout={logout} user={user} api={api}>
          <TranscoderFleetPage api={api} user={user} />
        </MainLayout>
      } />
      <Route path="/distribution/channel/:chId" element={
        <MainLayout currentPage="transcoders" onLogout={logout} user={user} api={api}>
          <TranscoderChannelDetailPage api={api} user={user} />
        </MainLayout>
      } />
      <Route path="/distribution/edge/:edgeId" element={
        <MainLayout currentPage="transcoders" onLogout={logout} user={user} api={api}>
          <EdgeDetailPage api={api} user={user} />
        </MainLayout>
      } />
      <Route path="/distribution/box/:boxId" element={
        <MainLayout currentPage="transcoders" onLogout={logout} user={user} api={api}>
          <BoxDetailPage api={api} user={user} />
        </MainLayout>
      } />
      {/* ── Radio (RailStream Wire) ─────────────────────────── */}
      <Route path="/radio/boxes" element={
        <MainLayout currentPage="radio-boxes" onLogout={logout} user={user} api={api}>
          <RadioBoxesPage api={api} user={user} />
        </MainLayout>
      } />
      <Route path="/radio/feeds" element={
        <MainLayout currentPage="radio-feeds" onLogout={logout} user={user} api={api}>
          <RadioFeedsPage api={api} user={user} />
        </MainLayout>
      } />
      <Route path="/radio/feeds/:trunkId" element={
        <MainLayout currentPage="radio-feeds" onLogout={logout} user={user} api={api}>
          <RadioFeedDetailPage api={api} user={user} />
        </MainLayout>
      } />
      <Route path="/radio/operators" element={
        <MainLayout currentPage="radio-operators" onLogout={logout} user={user} api={api}>
          <RadioOperatorsPage api={api} user={user} />
        </MainLayout>
      } />
      {/* Legacy routes */}
      <Route path="/transcoders/channel/:chId" element={
        <MainLayout currentPage="transcoders" onLogout={logout} user={user} api={api}>
          <TranscoderChannelDetailPage api={api} user={user} />
        </MainLayout>
      } />
      <Route path="/transcoders/edge/:edgeId" element={
        <MainLayout currentPage="transcoders" onLogout={logout} user={user} api={api}>
          <EdgeDetailPage api={api} user={user} />
        </MainLayout>
      } />
      <Route path="/transcoders/box/:boxId" element={
        <MainLayout currentPage="transcoders" onLogout={logout} user={user} api={api}>
          <BoxDetailPage api={api} user={user} />
        </MainLayout>
      } />
      <Route path="/transcoders/:tcId" element={
        <MainLayout currentPage="transcoders" onLogout={logout} user={user} api={api}>
          <TranscoderChannelDetailPage api={api} user={user} />
        </MainLayout>
      } />
      <Route path="/settings" element={
        <MainLayout currentPage="settings" onLogout={logout} user={user} api={api}>
          <SettingsPage api={api} />
        </MainLayout>
      } />
      <Route path="/integrations" element={
        <MainLayout currentPage="integrations" onLogout={logout} user={user} api={api}>
          <IntegrationsPage api={api} />
        </MainLayout>
      } />
      <Route path="/analytics" element={
        <MainLayout currentPage="analytics" onLogout={logout} user={user} api={api}>
          <AnalyticsPage api={api} user={user} />
        </MainLayout>
      } />
      <Route path="/devlog" element={
        <MainLayout currentPage="devlog" onLogout={logout} user={user} api={api}>
          <DevLogPage />
        </MainLayout>
      } />
      <Route path="/api-reference" element={
        <MainLayout currentPage="api-reference" onLogout={logout} user={user} api={api}>
          <ApiReferencePage api={api} />
        </MainLayout>
      } />
      <Route path="/documentation" element={
        <MainLayout currentPage="documentation" onLogout={logout} user={user} api={api}>
          <DocumentationPage />
        </MainLayout>
      } />
      <Route path="/performance" element={
        <MainLayout currentPage="performance" onLogout={logout} user={user} api={api}>
          <PerformancePage api={api} />
        </MainLayout>
      } />
      <Route path="/performance/channel/:channelId" element={
        <MainLayout currentPage="performance" onLogout={logout} user={user} api={api}>
          <ChannelAnalyticsPage api={api} />
        </MainLayout>
      } />
    </Routes>
  );
};

// Change Password Modal
const ChangePasswordModal = ({ isOpen, onChangePassword, onLogout }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    
    setLoading(true);
    const result = await onChangePassword(currentPassword, newPassword);
    if (result.error) {
      setError(result.error);
    } else {
      toast.success("Password changed successfully");
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md p-5 sm:p-8 bg-zinc-900 rounded-xl border border-zinc-800">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Key className="w-8 h-8 text-amber-500" />
          </div>
          <h2 className="text-xl font-bold text-white">Password Change Required</h2>
          <p className="text-zinc-400 text-sm mt-2">
            For security reasons, you must change your password before continuing.
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-zinc-300">Current Password</Label>
            <Input
              data-testid="current-password-input"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter current password"
              className="bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-zinc-300">New Password</Label>
            <Input
              data-testid="new-password-input"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              className="bg-zinc-800 border-zinc-700 text-white"
            />
            <p className="text-zinc-500 text-xs">
              Must be at least 8 characters with uppercase, lowercase, and a number
            </p>
          </div>
          
          <div className="space-y-2">
            <Label className="text-zinc-300">Confirm New Password</Label>
            <Input
              data-testid="confirm-password-input"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              className="bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
          
          {error && (
            <p data-testid="password-error" className="text-red-500 text-sm text-center">{error}</p>
          )}
          
          <Button 
            data-testid="change-password-submit"
            type="submit" 
            className="w-full h-12 bg-amber-500 hover:bg-amber-400 text-black font-medium"
            disabled={loading}
          >
            {loading ? "Changing Password..." : "Change Password"}
          </Button>
          
          <Button 
            type="button"
            variant="ghost"
            className="w-full text-zinc-400 hover:text-white"
            onClick={onLogout}
          >
            Logout Instead
          </Button>
        </form>
      </div>
    </div>
  );
};

// Mandatory 2FA Setup Screen — blocks all access until TOTP is configured
const Force2FASetup = ({ api, onComplete, onLogout }) => {
  const [step, setStep] = useState("intro"); // intro, scan, verify
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const startSetup = async () => {
    try {
      const res = await api.post("/auth/setup-2fa");
      setQrCode(res.data.qr_code);
      setSecret(res.data.secret);
      setStep("scan");
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to start 2FA setup");
    }
  };

  const verifyAndEnable = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/enable-2fa", { code });
      onComplete();
    } catch (e) {
      setError(e.response?.data?.detail || "Invalid code. Please try again.");
      setCode("");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 px-4 py-8">
      <div className="w-full max-w-lg p-5 sm:p-8 bg-zinc-900 rounded-xl border border-zinc-700">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-amber-500" />
          </div>
          <h2 className="text-xl font-bold text-white">Two-Factor Authentication Required</h2>
          <p className="text-zinc-300 text-sm mt-2">
            Your account requires two-factor authentication to continue. This is mandatory for all 
            RailStream Studio™ users to protect critical broadcast infrastructure.
          </p>
        </div>

        {step === "intro" && (
          <div className="space-y-4">
            <div className="bg-zinc-800 rounded-lg p-4 text-sm text-zinc-300 space-y-2">
              <p className="font-medium text-white">You will need:</p>
              <p>1. An authenticator app (Google Authenticator, Authy, or 1Password)</p>
              <p>2. Your mobile device to scan a QR code</p>
              <p>3. Enter the 6-digit code to verify</p>
            </div>
            {error && <p className="text-red-400 text-sm text-center">{error}</p>}
            <Button onClick={startSetup} className="w-full h-12 bg-amber-500 hover:bg-amber-400 text-black font-semibold">
              Set Up Two-Factor Authentication
            </Button>
            <Button variant="ghost" onClick={onLogout} className="w-full text-zinc-400 hover:text-white">
              Logout
            </Button>
          </div>
        )}

        {step === "scan" && (
          <form onSubmit={verifyAndEnable} className="space-y-4">
            <div className="flex justify-center">
              <img src={qrCode} alt="2FA QR Code" className="w-48 h-48 rounded-lg border border-zinc-700" />
            </div>
            <div className="bg-zinc-800 rounded-lg p-3">
              <p className="text-zinc-400 text-xs text-center mb-1">Manual entry key:</p>
              <p className="break-all text-amber-400 text-sm font-mono text-center tracking-wider select-all">{secret}</p>
            </div>
            <p className="text-zinc-300 text-sm text-center">
              Scan the QR code with your authenticator app, then enter the 6-digit code below.
            </p>
            <Input
              data-testid="force-2fa-code"
              type="text"
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="bg-zinc-800 border-zinc-600 text-white text-center text-2xl tracking-widest h-14"
              maxLength={6}
              autoFocus
            />
            {error && <p className="text-red-400 text-sm text-center font-medium">{error}</p>}
            <Button type="submit" disabled={loading || code.length !== 6}
              className="w-full h-12 bg-amber-500 hover:bg-amber-400 text-black font-semibold">
              {loading ? "Verifying..." : "Enable Two-Factor Authentication"}
            </Button>
            <Button variant="ghost" onClick={onLogout} className="w-full text-zinc-400 hover:text-white">
              Logout
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};

// Main App
function App() {
  const auth = useAuth();

  return (
    <div className="App">
      <BrowserRouter>
        {auth.isAuthenticated ? (
          auth.requires2FASetup ? (
            <Force2FASetupWrapper auth={auth} />
          ) : (
            <>
              <AppRouter auth={auth} />
              <ChangePasswordModal 
                isOpen={auth.requiresPasswordChange}
                onChangePassword={auth.changePassword}
                onLogout={auth.logout}
              />
            </>
          )
        ) : (
          <Routes>
            <Route path="/platform" element={<PlatformPage />} />
            <Route path="/terms" element={<PolicyPage policy="terms" />} />
            <Route path="/privacy" element={<PolicyPage policy="privacy" />} />
            <Route path="/acceptable-use" element={<PolicyPage policy="acceptable-use" />} />
            <Route path="/feed-provider-agreement" element={<PolicyPage policy="feed-provider-agreement" />} />
            <Route path="*" element={<LoginPage
              onLogin={auth.login}
              requires2FA={auth.requires2FA}
              onVerify2FA={auth.verify2FA}
              onCancel2FA={auth.cancel2FA}
            />} />
          </Routes>
        )}
      </BrowserRouter>
      <Toaster 
        position="bottom-right"
        expand={false}
        closeButton
        duration={2500}
        toastOptions={{
          style: {
            background: '#18181b',
            border: '1px solid #27272a',
            color: '#fafafa'
          }
        }}
      />
    </div>
  );
}

// Wrapper to give Force2FASetup access to authenticated api
const Force2FASetupWrapper = ({ auth }) => {
  const api = useApi(auth.getAuthHeader);
  return (
    <Force2FASetup 
      api={api} 
      onComplete={auth.complete2FASetup} 
      onLogout={auth.logout} 
    />
  );
};

export default App;
